import { useRef, useState } from "react"
import { Upload, Trash2 } from "lucide-react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useUploadDocuments } from "../hooks/use-upload-documents"

const ACCEPTED_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".csv", ".txt", ".md"]
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",")

const hasAcceptedExtension = (file: File): boolean => {
  const lower = file.name.toLowerCase()
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type Props = {
  open: boolean
  kbId: string
  onClose: () => void
}

export const UploadDocumentsDialog = ({ open, kbId, onClose }: Props) => {
  const [files, setFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { mutate: upload, isPending } = useUploadDocuments(kbId)

  const handleClose = () => {
    setFiles([])
    setIsDragging(false)
    onClose()
  }

  const addFiles = (incoming: File[]) => {
    const accepted: File[] = []
    const rejected: string[] = []
    for (const file of incoming) {
      if (hasAcceptedExtension(file)) accepted.push(file)
      else rejected.push(file.name)
    }
    if (rejected.length > 0) {
      toast.error(`Unsupported file type: ${rejected.join(", ")}`)
    }
    setFiles((prev) => [...prev, ...accepted])
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(Array.from(e.target.files))
    e.target.value = ""
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    if (e.dataTransfer.files) addFiles(Array.from(e.dataTransfer.files))
  }

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUpload = () => {
    if (files.length === 0) return
    upload(files, {
      onSuccess: (result) => {
        if (result.failed.length === 0) {
          toast.success(`Uploaded ${result.uploaded.length} file(s)`)
          handleClose()
        } else {
          toast.warning(
            `Uploaded ${result.uploaded.length} of ${result.total}. Failed: ${result.failed
              .map((f) => f.filename)
              .join(", ")}`,
          )
          setFiles([])
        }
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div
            className={cn(
              "flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
              isDragging ? "border-primary bg-primary/5" : "border-border",
            )}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <Upload className="size-8 text-muted-foreground" />
            <p className="text-sm">
              <span className="font-medium">Click to browse</span> or drag and drop
            </p>
            <p className="text-xs text-muted-foreground">
              PDF, DOCX, XLSX, CSV, TXT, MD
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>

          {files.length > 0 && (
            <div className="flex max-h-56 flex-col gap-1 overflow-y-auto rounded-lg border p-2">
              {files.map((file, i) => (
                <div
                  key={`${file.name}-${i}`}
                  className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-secondary/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-2 break-words text-sm">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    onClick={() => removeFile(i)}
                    disabled={isPending}
                    aria-label={`Remove ${file.name}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={isPending || files.length === 0}>
            {isPending ? "Uploading…" : `Upload${files.length > 0 ? ` (${files.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
